import {
  Edit as EditIcon,
  Sync as SyncIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  TextField,
  Switch,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import React, { useState, useEffect } from "react";

import {
  FEATURE_DEFINITIONS,
  getAllPlans,
  formatFeatureValue,
  getFeaturesByCategory,
} from "../constants/subscriptionPlans";
import { adminApiClient } from "../services/apiClient";

// Types based on hardcoded plans instead of database
interface FeatureSet {
  [key: string]: number | boolean; // Derived from FeatureValues in constants
}

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string | null;
  monthlyPrice: number;
  yearlyPrice: number;
  stripePriceIds: {
    monthly: string | null;
    yearly: string | null;
  };
  displayOrder: number;
  isDefault: boolean;
  features: FeatureSet;
  // Database-related fields (for compatibility with existing API calls)
  stripePriceIdMonthly?: string | null;
  stripePriceIdYearly?: string | null;
  textsPerMonth?: number;
  isActive?: boolean;
  isFreeDefault?: boolean;
  _count?: {
    subscriptions: number;
  };
}

// Legacy interface for backward compatibility (kept for reference)
// interface PlanFeature {
//   id: string;
//   featureKey: string;
//   featureName: string;
//   isEnabled: boolean;
//   value: string;
// }

// NewPlan interface removed (plan creation dialog currently not active)

const SubscriptionPlansPage: React.FC = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editDialog, setEditDialog] = useState(false);
  // Removed addDialog state (dialog not implemented yet)
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(
    null,
  );
  // Removed newPlan state (creation dialog not in use)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Загрузка планов подписки (comments may stay in Russian per policy)
  const loadPlans = async (): Promise<void> => {
    try {
      setLoading(true);

      // Fetch basic plan info from API (prices, stripe IDs, subscriber count)
      let apiPlans: Array<Record<string, unknown>> = [];
      try {
        const response = await adminApiClient.get("/admin/subscription-plans");
        apiPlans = response.data.plans || [];
      } catch {
        // Non-fatal: fallback to constants only
      }

      // Комбинируем константы с данными из API
      const hardcodedPlans = getAllPlans();
      const combinedPlans: SubscriptionPlan[] = hardcodedPlans.map(
        (hardcodedPlan) => {
          // Ищем соответствующий план из API
          const apiPlan = apiPlans.find(
            (p) =>
              (typeof p.id === "string" && p.id === hardcodedPlan.id) ||
              (typeof p.name === "string" && p.name === hardcodedPlan.name),
          ) as Record<string, unknown> | undefined;

          return {
            id: hardcodedPlan.id,
            name: hardcodedPlan.name,
            description: hardcodedPlan.description,
            monthlyPrice:
              typeof apiPlan?.monthlyPrice === "number"
                ? (apiPlan.monthlyPrice as number)
                : hardcodedPlan.monthlyPrice,
            yearlyPrice:
              typeof apiPlan?.yearlyPrice === "number"
                ? (apiPlan.yearlyPrice as number)
                : hardcodedPlan.yearlyPrice,
            stripePriceIds: {
              monthly:
                (typeof apiPlan?.stripePriceIdMonthly === "string"
                  ? (apiPlan.stripePriceIdMonthly as string)
                  : null) || hardcodedPlan.stripePriceIds.monthly,
              yearly:
                (typeof apiPlan?.stripePriceIdYearly === "string"
                  ? (apiPlan.stripePriceIdYearly as string)
                  : null) || hardcodedPlan.stripePriceIds.yearly,
            },
            displayOrder: hardcodedPlan.displayOrder,
            isDefault: hardcodedPlan.isDefault,
            features: hardcodedPlan.features, // Always from constants!

            // Legacy fields for compatibility
            stripePriceIdMonthly:
              (typeof apiPlan?.stripePriceIdMonthly === "string"
                ? (apiPlan.stripePriceIdMonthly as string)
                : null) || hardcodedPlan.stripePriceIds.monthly,
            stripePriceIdYearly:
              (typeof apiPlan?.stripePriceIdYearly === "string"
                ? (apiPlan.stripePriceIdYearly as string)
                : null) || hardcodedPlan.stripePriceIds.yearly,
            textsPerMonth: hardcodedPlan.features.TEXT_CREATION_LIMIT || 0,
            isActive:
              typeof apiPlan?.isActive === "boolean"
                ? (apiPlan.isActive as boolean)
                : true,
            isFreeDefault: hardcodedPlan.isDefault,
            _count:
              apiPlan && typeof apiPlan === "object" && "_count" in apiPlan
                ? (apiPlan._count as { subscriptions: number })
                : undefined,
          };
        },
      );

      setPlans(combinedPlans);
    } catch {
      setError("Failed to load subscription plans");
      // Store user-facing error already set above
    } finally {
      setLoading(false);
    }
  };

  // Синхронизация с константами
  const syncPlans = async (): Promise<void> => {
    try {
      setLoading(true);
      await adminApiClient.post("/admin/subscription-plans/sync");
      setSuccess("Plans have been synchronized with constants");
      await loadPlans();
    } catch {
      setError("Failed to sync plans");
      // Error surfaced via setError
    } finally {
      setLoading(false);
    }
  };

  // Инициализация базовых планов
  const initializePlans = async (): Promise<void> => {
    try {
      setLoading(true);
      await adminApiClient.post("/admin/subscription-plans/initialize");
      setSuccess("Base plans have been initialized");
      await loadPlans();
    } catch {
      setError("Failed to initialize plans");
      // Error surfaced via setError
    } finally {
      setLoading(false);
    }
  };

  // Full reset and reinitialization of plans
  const resetPlans = async (): Promise<void> => {
    if (
      !confirm(
        "WARNING: This will delete all plans without active subscriptions and recreate them from configuration. Continue?",
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      const response = await adminApiClient.post(
        "/admin/subscription-plans/reset",
      );
      setSuccess(
        `Plans have been re-initialized. Processed: ${response.data.plansProcessed} plans`,
      );
      await loadPlans();
    } catch {
      setError("Failed to reset plans");
      // Error surfaced via setError
    } finally {
      setLoading(false);
    }
  };

  // Обновление плана
  const updatePlan = async (
    planId: string,
    updates: Partial<SubscriptionPlan>,
  ): Promise<void> => {
    try {
      await adminApiClient.patch(
        `/admin/subscription-plans/${planId}`,
        updates,
      );
      setSuccess("Plan updated");
      await loadPlans();
      setEditDialog(false);
    } catch {
      setError("Failed to update plan");
      // Error surfaced via setError
    }
  };

  // Creating a new plan
  // createPlan logic removed (unused; add dialog not implemented)

  // Переключение активности плана
  const togglePlanActive = async (plan: SubscriptionPlan): Promise<void> => {
    await updatePlan(plan.id, { isActive: !plan.isActive });
  };

  useEffect(() => {
    loadPlans();
  }, []);

  const handleEditPlan = (plan: SubscriptionPlan): void => {
    setSelectedPlan(plan);
    setEditDialog(true);
  };

  const getImplementationStatus = (features: FeatureSet): string => {
    if (!features) return "0/0"; // safeguard

    const featureEntries = Object.entries(features);
    const implemented = featureEntries.filter(([, value]) => {
      // Feature считается implemented если оно enabled (true) или имеет положительное значение
      if (typeof value === "boolean") return value;
      if (typeof value === "number") return value > 0;
      return false;
    }).length;

    const total = featureEntries.length;
    return `${implemented}/${total}`;
  };

  // Функция для правильного форматирования цен
  const formatPrice = (price: number): string => {
    // Если цена больше 100, считаем что она в центах и конвертируем в доллары
    if (price >= 100) {
      return (price / 100).toFixed(2);
    }
    // Иначе цена уже в долларах
    return price.toFixed(2);
  };

  // Функция для получения иконки статуса функции
  const getFeatureIcon = (
    featureKey: string,
    value: number | boolean,
  ): React.ReactElement => {
    const definition =
      FEATURE_DEFINITIONS[featureKey as keyof typeof FEATURE_DEFINITIONS];

    if (!definition) {
      return <WarningIcon color="warning" />;
    }

    if (definition.type === "boolean") {
      return value ? (
        <CheckCircleIcon color="success" />
      ) : (
        <CancelIcon color="disabled" />
      );
    }

    if (definition.type === "limit") {
      return typeof value === "number" && (value > 0 || value === -1) ? (
        <CheckCircleIcon color="success" />
      ) : (
        <CancelIcon color="disabled" />
      );
    }

    return <CheckCircleIcon color="success" />;
  };

  // Функция для отображения значения функции
  const getFeatureValueDisplay = (
    _featureKey: string,
    value: number | boolean,
  ): string => formatFeatureValue(value);

  // Функция для группировки функций по категориям
  type GroupedFeature = {
    key: string;
    value: number | boolean;
    definition: { name: string; description: string } & Record<string, unknown>;
  };
  const groupFeaturesByCategory = (
    features: FeatureSet,
  ): Array<[string, GroupedFeature[]]> => {
    if (!features) return [];

    const categoriesFromConstants = getFeaturesByCategory();

    // Группируем features план по категориям из констант
    const groupedFeatures: Array<[string, GroupedFeature[]]> = [];

    Object.entries(categoriesFromConstants).forEach(
      ([categoryName, categoryFeatures]) => {
        const matchingFeatures = categoryFeatures.flatMap(
          (definition): GroupedFeature[] => {
            const entry = Object.entries(FEATURE_DEFINITIONS).find(
              ([, candidate]): boolean => candidate === definition,
            );
            if (
              !entry ||
              !Object.prototype.hasOwnProperty.call(features, entry[0])
            )
              return [];
            return [{ key: entry[0], value: features[entry[0]], definition }];
          },
        );

        if (matchingFeatures.length > 0) {
          groupedFeatures.push([
            categoryName.charAt(0).toUpperCase() + categoryName.slice(1),
            matchingFeatures,
          ]);
        }
      },
    );

    return groupedFeatures;
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Заголовок */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Typography variant="h4" component="h1">
            Subscription Plans Management
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<SyncIcon />}
              onClick={syncPlans}
              disabled={loading}
            >
              Sync
            </Button>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={initializePlans}
              disabled={loading}
              color="primary"
            >
              Initialize
            </Button>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={resetPlans}
              disabled={loading}
              color="warning"
            >
              Reset plans
            </Button>
          </Box>
        </Box>

        {/* Plans table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Plan</TableCell>
                  <TableCell>Prices (USD)</TableCell>
                  <TableCell>Limits</TableCell>
                  <TableCell>Stripe Price IDs</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Subscribers</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plans
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((plan) => (
                    <TableRow
                      key={plan.id}
                      sx={{
                        backgroundColor: plan.isFreeDefault
                          ? "rgba(76, 175, 80, 0.08)"
                          : "inherit",
                      }}
                    >
                      <TableCell>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600 }}
                          >
                            {plan.name}
                            {plan.isFreeDefault && (
                              <Chip
                                label="Default"
                                color="success"
                                size="small"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {plan.description}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          ${formatPrice(plan.monthlyPrice)}/mo
                        </Typography>
                        <Typography variant="body2">
                          ${formatPrice(plan.yearlyPrice)}/yr
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {plan.textsPerMonth} texts/mo
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Implemented: {getImplementationStatus(plan.features)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" component="div">
                          M: {plan.stripePriceIdMonthly || "Not set"}
                        </Typography>
                        <Typography variant="caption" component="div">
                          Y: {plan.stripePriceIdYearly || "Not set"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={plan.isActive}
                          onChange={() => togglePlanActive(plan)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {plan._count?.subscriptions || 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleEditPlan(plan)}
                          title="Edit plan"
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={plans.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) =>
              setRowsPerPage(parseInt(e.target.value, 10))
            }
          />
        </Paper>

        {/* Edit dialog */}
        <Dialog
          open={editDialog}
          onClose={() => setEditDialog(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">
                Edit plan: {selectedPlan?.name}
              </Typography>
              <Chip
                label={`Implemented: ${selectedPlan ? getImplementationStatus(selectedPlan.features) : "0/0"}`}
                color="primary"
                size="small"
              />
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedPlan && (
              <Box sx={{ pt: 2 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Monthly price (USD)"
                      type="number"
                      inputProps={{ step: "0.01" }}
                      value={formatPrice(selectedPlan.monthlyPrice)}
                      onChange={(e) => {
                        const dollarValue = parseFloat(e.target.value) || 0;
                        const centValue = Math.round(dollarValue * 100);
                        setSelectedPlan({
                          ...selectedPlan,
                          monthlyPrice: centValue,
                        });
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Yearly price (USD)"
                      type="number"
                      inputProps={{ step: "0.01" }}
                      value={formatPrice(selectedPlan.yearlyPrice)}
                      onChange={(e) => {
                        const dollarValue = parseFloat(e.target.value) || 0;
                        const centValue = Math.round(dollarValue * 100);
                        setSelectedPlan({
                          ...selectedPlan,
                          yearlyPrice: centValue,
                        });
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Stripe Price ID (monthly)"
                      value={selectedPlan.stripePriceIdMonthly || ""}
                      onChange={(e) =>
                        setSelectedPlan({
                          ...selectedPlan,
                          stripePriceIdMonthly: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Stripe Price ID (yearly)"
                      value={selectedPlan.stripePriceIdYearly || ""}
                      onChange={(e) =>
                        setSelectedPlan({
                          ...selectedPlan,
                          stripePriceIdYearly: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Description"
                      multiline
                      rows={2}
                      value={selectedPlan.description || ""}
                      onChange={(e) =>
                        setSelectedPlan({
                          ...selectedPlan,
                          description: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  {/* Plan features section */}
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 3 }}>
                      <Typography variant="h6" color="primary">
                        Plan features and limits
                      </Typography>
                    </Divider>

                    <Paper
                      variant="outlined"
                      sx={{ p: 2, maxHeight: 400, overflow: "auto" }}
                    >
                      {groupFeaturesByCategory(selectedPlan.features).map(
                        ([categoryName, categoryFeatures]) => (
                          <Box key={categoryName} sx={{ mb: 2 }}>
                            <Typography
                              variant="subtitle2"
                              color="primary"
                              sx={{ mb: 1, fontWeight: 600 }}
                            >
                              {categoryName}
                            </Typography>
                            <List dense sx={{ pl: 2 }}>
                              {categoryFeatures.map((feature) => (
                                <ListItem key={feature.key} sx={{ py: 0.5 }}>
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    {getFeatureIcon(feature.key, feature.value)}
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          fontWeight="medium"
                                        >
                                          {feature.definition.name}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          fontWeight="600"
                                        >
                                          {getFeatureValueDisplay(
                                            feature.key,
                                            feature.value,
                                          )}
                                        </Typography>
                                      </Box>
                                    }
                                    secondary={
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                          mt: 0.5,
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          {feature.key}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          • {feature.definition.description}
                                        </Typography>
                                      </Box>
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                            {categoryName !== "System" && (
                              <Divider sx={{ mt: 1 }} />
                            )}
                          </Box>
                        ),
                      )}

                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "grey.50",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          💡 This information is for reference only and is based
                          on the plan configuration in code. Changing prices and
                          Stripe IDs above does not affect plan features.
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() =>
                selectedPlan && updatePlan(selectedPlan.id, selectedPlan)
              }
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Toasts */}
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
        >
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!success}
          autoHideDuration={4000}
          onClose={() => setSuccess(null)}
        >
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default SubscriptionPlansPage;
